import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import {
  adminProblemsApi,
  type AdminProblem,
  type AdminTestCase,
  type CreateProblemRequest,
  type Difficulty,
  type ProblemStatus,
} from '@/lib/api/admin-problems';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollFadeArea } from '@/components/ScrollFadeArea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProblemMetadataSidebar } from '@/features/AdminProblems/ProblemMetadataSidebar';
import { ProblemDescriptionTab } from '@/features/AdminProblems/ProblemDescriptionTab';
import { ProblemTestCasesTab } from '@/features/AdminProblems/ProblemTestCasesTab';
import { ProblemStarterCodeTab } from '@/features/AdminProblems/ProblemStarterCodeTab';
import { ProblemSqlTab } from '@/features/AdminProblems/ProblemSqlTab';
import { PRIMARY_ACTION } from './actionStyles';

const STATUS_ACTIONS: Record<ProblemStatus, { label: string; next: ProblemStatus }[]> = {
  draft:     [{ label: 'Publish', next: 'published' }],
  published: [{ label: 'Unpublish', next: 'draft' }, { label: 'Archive', next: 'archived' }],
  archived:  [{ label: 'Restore to Draft', next: 'draft' }],
};

interface Props {
  problemId: string;
  /** Navigation is injected so each route can use its own typed `to`. */
  onBack: () => void;
  /**
   * Rendered under the header when the problem is a draft. The trainer surface
   * uses it to warn that students cannot open an unpublished problem.
   */
  draftNotice?: React.ReactNode;
}

/**
 * The problem editor, shared by /admin/problems/$problemId and
 * /trainer/problems/$problemId. A trainer who does not own the problem gets a
 * 404 from the API, so this never has to check ownership itself.
 */
export function ProblemEditorForm({ problemId, onBack, draftNotice }: Props) {
  const [problem, setProblem] = useState<AdminProblem | null>(null);
  const [testCases, setTestCases] = useState<AdminTestCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  const [form, setForm] = useState<CreateProblemRequest>({
    title: '', description: '', constraints: '', examples: [], follow_up: '', difficulty: 'easy',
    tags: [], company_tags: [], company_frequency: {}, time_limit_ms: 2000, memory_limit_mb: 256, starter_code: {},
  });

  async function load() {
    try {
      setLoading(true);
      const p = await adminProblemsApi.get(problemId);
      setProblem(p);
      setTestCases(p.test_cases ?? []);
      // Prefer the structured `content` (what the student page renders) for
      // statement/constraints text — `description`/`constraints` are only
      // used as a fallback for problems that predate this editor.
      setForm({
        title:           p.title,
        description:     p.content?.statement || p.description,
        constraints:     p.content?.constraints?.join('\n') || p.constraints || '',
        examples:        p.content?.examples ?? [],
        follow_up:       p.content?.followUp ?? '',
        difficulty:      p.difficulty,
        tags:              p.tags,
        company_tags:      p.company_tags,
        company_frequency: p.company_frequency ?? {},
        time_limit_ms:   p.time_limit_ms,
        memory_limit_mb: p.memory_limit_mb,
        starter_code:    p.starter_code ?? {},
        execution_mode:       p.execution_mode ?? 'stdio',
        sql_setup:            p.sql_setup ?? '',
        sql_reference_query:  p.sql_reference_query ?? '',
        sql_options:          p.sql_options ?? { order_matters: false },
      });
    } catch {
      toast.error('Failed to load problem');
    } finally {
      setLoading(false);
    }
  }

  async function refreshTestCases() {
    const p = await adminProblemsApi.get(problemId);
    setTestCases(p.test_cases ?? []);
  }

  useEffect(() => { void load(); }, [problemId]);

  function handleChange(field: string, value: unknown) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function handleSqlFieldChange(
    field: 'execution_mode' | 'sql_setup' | 'sql_reference_query' | 'order_matters',
    value: string | boolean,
  ) {
    if (field === 'order_matters') {
      setForm(prev => ({
        ...prev,
        sql_options: { ...prev.sql_options, order_matters: value as boolean },
      }));
      return;
    }
    setForm(prev => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    try {
      setSaving(true);
      const updated = await adminProblemsApi.update(problemId, form);
      setProblem(updated);
      toast.success('Problem saved');
    } catch {
      toast.error('Failed to save problem');
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusChange(next: ProblemStatus) {
    try {
      setChangingStatus(true);
      const updated = await adminProblemsApi.changeStatus(problemId, next);
      setProblem(updated);
      toast.success(`Problem ${next}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setChangingStatus(false);
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col h-full -m-6">
        <div className="flex items-center gap-3 px-5 py-3 border-b bg-white/80">
          <Skeleton className="h-5 w-48" />
        </div>
        <div className="flex flex-1 p-6 gap-4">
          <Skeleton className="w-[220px] h-full" />
          <Skeleton className="flex-1 h-full" />
        </div>
      </div>
    );
  }

  if (!problem) return null;

  const actions = STATUS_ACTIONS[problem.status as ProblemStatus] ?? [];

  return (
    <div className="flex flex-col h-full -m-6">
      <div className="flex items-center justify-between px-5 py-3 border-b bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-2 text-[13px]">
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Problems
          </button>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold text-foreground truncate max-w-[280px]">{problem.title}</span>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="text-[13px]"
            onClick={handleSave}
            disabled={saving || changingStatus}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          {actions.map(action => (
            <Button
              key={action.next}
              size="sm"
              className={cn(
                'text-[13px]',
                action.next === 'published'
                  ? PRIMARY_ACTION
                  : 'bg-foreground text-background hover:bg-foreground/90',
              )}
              onClick={() => handleStatusChange(action.next)}
              disabled={saving || changingStatus}
            >
              {changingStatus ? '…' : action.label}
            </Button>
          ))}
        </div>
      </div>

      {draftNotice && problem.status === 'draft' && draftNotice}

      <div className="flex flex-1 overflow-hidden">
        <div className="w-[220px] shrink-0 overflow-y-auto border-r">
          <ProblemMetadataSidebar
            difficulty={form.difficulty as Difficulty}
            tags={form.tags}
            companyTags={form.company_tags}
            companyFrequency={form.company_frequency}
            timeLimitMs={form.time_limit_ms}
            memoryLimitMb={form.memory_limit_mb}
            slug={problem.slug}
            status={problem.status as ProblemStatus}
            onChange={handleChange}
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          <Tabs defaultValue="description" className="h-full flex flex-col">
            <TabsList className="mx-4 mt-3 w-fit">
              <TabsTrigger value="description" className="text-[13px]">Description</TabsTrigger>
              <TabsTrigger value="testcases" className="text-[13px]">
                Test Cases
                {testCases.length > 0 && (
                  <span className="ml-1.5 text-xs bg-muted px-1.5 py-0.5 rounded-full">
                    {testCases.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="starter" className="text-[13px]">Starter Code</TabsTrigger>
              <TabsTrigger value="sql" className="text-[13px]">SQL</TabsTrigger>
            </TabsList>
            <TabsContent value="description" className="relative flex-1 overflow-hidden">
              <ScrollFadeArea className="h-full overflow-y-auto">
                <ProblemDescriptionTab
                  title={form.title}
                  description={form.description}
                  constraints={form.constraints}
                  examples={form.examples}
                  followUp={form.follow_up}
                  onChange={handleChange}
                />
              </ScrollFadeArea>
            </TabsContent>
            <TabsContent value="testcases" className="relative flex-1 overflow-hidden">
              <ScrollFadeArea className="h-full overflow-y-auto">
                <ProblemTestCasesTab
                  problemId={problemId}
                  testCases={testCases}
                  onRefresh={refreshTestCases}
                />
              </ScrollFadeArea>
            </TabsContent>
            <TabsContent value="starter" className="relative flex-1 overflow-hidden">
              <ScrollFadeArea className="h-full overflow-y-auto">
                <ProblemStarterCodeTab
                  starterCode={form.starter_code}
                  onChange={(lang, code) =>
                    handleChange('starter_code', { ...form.starter_code, [lang]: code })
                  }
                />
              </ScrollFadeArea>
            </TabsContent>
            <TabsContent value="sql" className="relative flex-1 overflow-hidden">
              <ScrollFadeArea className="h-full overflow-y-auto">
                <ProblemSqlTab
                  executionMode={form.execution_mode ?? 'stdio'}
                  sqlSetup={form.sql_setup ?? ''}
                  sqlReferenceQuery={form.sql_reference_query ?? ''}
                  orderMatters={form.sql_options?.order_matters ?? false}
                  onChange={handleSqlFieldChange}
                />
              </ScrollFadeArea>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
